import { Injectable, NgModule } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Resolve,
  Router,
  RouterModule,
  Routes,
} from "@angular/router";
import { LoggedInGuard } from "../auth/auth.guards";
import { AccountPrerequisitesGuard } from "../prerequisites/account-prerequisites.guard";
import { RosterComponent } from "./roster/roster.component";
import { SessionService } from "../session.service";
import { AppService } from "../apps/app.service";
import { map, take } from "rxjs/operators";
import { CustomerComponent } from "./customer/customer.component";
import { CustomersListComponent } from "./customers-list/customers-list.component";
import { CreateBroadcastComponent } from "./create-broadcast/create-broadcast.component";
import {
  AccountAppResolve,
  BeaconRegionsResolve,
  GeofencesResolve,
  InitialAudienceResolve,
  SiganlTemplatesResolve,
  SignalCategoriesResolve,
  TagsResolve,
} from "../broadcasts/broadcasts.routes";
import { from, Observable } from "rxjs";
import { PermissionService } from "../permission.service";
import { TemplateListGuard } from "../content/content.routes";
import { AccountSignalCategoryListGuard } from "../shared/guards/account-signal-category.guards";
import {
  BroadcastListGuard,
  BroadcastWriteGuard,
} from "../broadcasts/broadcasts.guards";
import { CustomerInsightsComponent } from "./customer-insights/customer-insights.component";

@Injectable({
  providedIn: "root",
})
export class AccountAppUsersListGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(
      this.PermissionService.categories.accountAppUsers.hasList(),
    ).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({
  providedIn: "root",
})
export class AccountAppUsersDetailGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    return from(
      this.PermissionService.categories.accountAppUsers.hasDetail(),
    ).pipe(
      map((res) => {
        if (!res) {
          this.router.navigate([""]);
        }
        return res;
      }),
    );
  }
}

@Injectable({ providedIn: "root" })
export class CustomerResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppService.getUser(
      this.SessionService.currentAccountValue!.accountID,
      route.params.customerID,
    );
  }
}

@Injectable({ providedIn: "root" })
export class AudiencesResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve() {
    return this.AppService.getAudiences(
      this.SessionService.currentAccountValue!.accountID,
      this.SessionService.currentAppValue!.id,
      {
        page: 0,
        perPage: 999999999,
      },
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class SignalHistoryResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppService.getAppUserSignalHistory(
      this.SessionService.currentAccountValue!.accountID,
      route.params.customerID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
export class LocationHistoryResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    return this.AppService.getAppUserLocationHistory(
      this.SessionService.currentAccountValue!.accountID,
      route.params.customerID,
    ).pipe(take(1));
  }
}

@Injectable({ providedIn: "root" })
class CustomerRosterPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.beaconRegions.hasList(),
      this.PermissionService.categories.geofences.hasList(),
      this.PermissionService.categories.broadcasts.hasWrite(),
      this.PermissionService.categories.broadcasts.hasList(),
      this.PermissionService.categories.accountSignalCategories.hasList(),
      this.PermissionService.categories.templates.hasList(),
      this.PermissionService.categories.accountAppUsers.hasDetail(),
    ]).then(
      ([
        hasBrList,
        hasGfList,
        hasQsWrite,
        hasQsList,
        hasSignalList,
        hasTemplateList,
        hasAauDetail,
      ]) => {
        return {
          hasBrList,
          hasGfList,
          hasQsWrite,
          hasQsList,
          hasSignalList,
          hasTemplateList,
          hasAauDetail,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
class SavedCustomerListsPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.broadcasts.hasWrite(),
      this.PermissionService.categories.broadcasts.hasDelete(),
      this.PermissionService.categories.broadcasts.hasList(),
      this.PermissionService.categories.accountSignalCategories.hasList(),
      this.PermissionService.categories.templates.hasList(),
    ]).then(
      ([
        hasBroadcastWrite,
        hasBroadcastDelete,
        hasBroadcastList,
        hasCategoriesList,
        hasTemplatesList,
      ]) => {
        return {
          hasBroadcastWrite,
          hasBroadcastDelete,
          hasBroadcastList,
          hasCategoriesList,
          hasTemplatesList,
        };
      },
    );
  }
}

@Injectable({ providedIn: "root" })
class CreateBroadcastPermissionsResolve implements Resolve<any> {
  constructor(private PermissionService: PermissionService) {}

  resolve() {
    return Promise.all([
      this.PermissionService.categories.templates.hasWrite(),
      this.PermissionService.categories.geofences.hasList(),
      this.PermissionService.categories.beaconRegions.hasList(),
    ]).then(([hasTemplatesWrite, hasGeofencesList, hasBeaconRegionsList]) => {
      return {
        hasTemplatesWrite,
        hasGeofencesList,
        hasBeaconRegionsList,
      };
    });
  }
}

@Injectable({ providedIn: "root" })
export class StartDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const start = new Date();
    start.setMonth(start.getMonth() - 1);
    start.setHours(0, 0, 0, 0);
    return start;
  }
}

@Injectable({ providedIn: "root" })
export class EndDateResolve implements Resolve<any> {
  constructor() {}

  resolve() {
    const end = new Date();
    end.setHours(23, 59, 59);
    return end;
  }
}

@Injectable({ providedIn: "root" })
export class CustomerInsightsResolve implements Resolve<any> {
  constructor(
    private AppService: AppService,
    private SessionService: SessionService,
  ) {}

  resolve(route: ActivatedRouteSnapshot) {
    const facilityIDs = this.SessionService.facilitiesValue?.length
      ? this.SessionService.facilitiesValue.map((facility) => {
          return facility.id;
        })
      : [];

    const start = route.parent!.data.start;
    const end = route.parent!.data.end;

    return this.AppService.getCustomerInsights(
      this.SessionService.currentAccountValue!.accountID,
      facilityIDs,
      start,
      end,
    ).pipe(take(1));
  }
}

const routes: Routes = [
  {
    path: "",
    canActivate: [LoggedInGuard, AccountPrerequisitesGuard],
    data: { sidebar: "customers" },
    children: [
      {
        path: "roster",
        canActivate: [AccountAppUsersListGuard],
        component: RosterComponent,
        data: { title: "Customer Roster" },
        resolve: {
          geofences: GeofencesResolve,
          beaconRegions: BeaconRegionsResolve,
          permissions: CustomerRosterPermissionsResolve,
        },
      },
      {
        path: "imports",
        loadChildren: () => import('./imports/imports.module').then(m => m.ImportsModule),
        data: { title: "Customer Imports" },
        canActivate: [AccountAppUsersDetailGuard],
      },
      {
        path: "customerLists",
        component: CustomersListComponent,
        data: { title: "Saved Customer Lists" },
        canActivate: [BroadcastListGuard],
        resolve: {
          audiences: AudiencesResolve,
          permissions: SavedCustomerListsPermissionsResolve,
        },
      },
      {
        path: "customerInsights",
        data: { title: "Customer Insights" },
        resolve: {
          start: StartDateResolve,
          end: EndDateResolve,
        },
        children: [
          {
            path: "",
            component: CustomerInsightsComponent,
            resolve: { customerInsights: CustomerInsightsResolve },
            canActivate: [AccountAppUsersListGuard],
          },
        ],
      },
      {
        path: "createBroadcast",
        component: CreateBroadcastComponent,
        resolve: {
          signalTemplates: SiganlTemplatesResolve,
          signalCategories: SignalCategoriesResolve,
          accountApp: AccountAppResolve,
          tags: TagsResolve,
          audiences: AudiencesResolve,
          geofences: GeofencesResolve,
          beaconRegions: BeaconRegionsResolve,
          initialAudience: InitialAudienceResolve,
          permissions: CreateBroadcastPermissionsResolve,
        },
        canActivate: [
          BroadcastWriteGuard,
          TemplateListGuard,
          BroadcastListGuard,
          AccountSignalCategoryListGuard,
        ],
      },
      {
        path: ":customerID",
        resolve: {
          customer: CustomerResolve,
          locationHistory: LocationHistoryResolve,
          signalHistory: SignalHistoryResolve,
        },
        canActivate: [AccountAppUsersDetailGuard],
        component: CustomerComponent,
        data: { title: "Customer" },
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CustomersRoutingModule {}

const customersModuleLazyLoadRoutes: Routes = [
  {
    path: "customers",
    loadChildren: () =>
      import("./customers.module").then((m) => m.CustomersModule),
  },
];

export { customersModuleLazyLoadRoutes };
